import { useState, useEffect, useCallback } from 'react';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  image: string;
}

interface StaggerTestimonialsProps {
  testimonials: Testimonial[];
}

const StaggerTestimonials: React.FC<StaggerTestimonialsProps> = ({ testimonials }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    const interval = setInterval(next, 5000);
    return () => clearInterval(interval);
  }, [next]);

  const getCardProps = (index: number) => {
    const total = testimonials.length;
    let rel = (index - activeIndex + total) % total;
    if (rel > Math.floor(total / 2)) rel -= total;

    const absRel = Math.abs(rel);
    const visible = 3;

    if (absRel > visible) return null;

    const xGap = 230;
    const translateX = rel * xGap;
    const translateY = absRel === 0 ? 0 : absRel * 12;
    const scale = absRel === 0 ? 1 : 1 - absRel * 0.07;
    const opacity = absRel === 0 ? 1 : absRel === 1 ? 0.85 : absRel === 2 ? 0.65 : 0.4;
    const zIndex = 20 - absRel * 4;
    const rotate = rel * 3.5;

    return {
      transform: `translateX(${translateX}px) translateY(${translateY}px) scale(${scale}) rotate(${rotate}deg)`,
      opacity,
      zIndex,
      isActive: absRel === 0,
      rel,
    };
  };

  return (
    <div className="flex flex-col items-center w-full">
      {/* Card Stack */}
      <div className="relative w-full flex items-center justify-center" style={{ height: '420px' }}>
        {testimonials.map((t, i) => {
          const props = getCardProps(i);
          if (!props) return null;
          const { transform, opacity, zIndex, isActive } = props;

          return (
            <div
              key={i}
              onClick={() => !isActive && setActiveIndex(i)}
              style={{
                transform,
                opacity,
                zIndex,
                transition: 'all 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
                width: '320px',
                position: 'absolute',
              }}
              className={`flex flex-col p-7 border-2 ${
                isActive
                  ? 'bg-foreground-900 text-background-50 border-foreground-900'
                  : 'bg-background-50 text-foreground-800 border-background-200 cursor-pointer hover:border-background-300'
              }`}
            >
              {/* Folded corner */}
              <span
                className="absolute block origin-top-right rotate-45 w-4 h-4 -top-2 right-8"
                style={{ background: isActive ? 'oklch(var(--foreground-900))' : 'oklch(var(--background-200))' }}
              />

              {/* Avatar */}
              <div className="mb-5">
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-14 h-14 object-cover object-top bg-background-100"
                  style={{ borderRadius: 0 }}
                />
              </div>

              {/* Quote */}
              <p className={`text-sm md:text-base leading-relaxed font-medium mb-6 flex-1 ${isActive ? 'text-background-50' : 'text-foreground-700'}`}>
                &ldquo;{t.quote}&rdquo;
              </p>

              {/* Attribution */}
              <p className={`text-xs italic mt-auto ${isActive ? 'text-background-50/60' : 'text-foreground-400'}`}>
                - {t.name}, {t.role}
              </p>
            </div>
          );
        })}
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={prev}
          aria-label="Previous testimonial"
          className="flex h-12 w-12 items-center justify-center text-lg border-2 border-background-200 bg-background-50 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all duration-300 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>
        <button
          onClick={next}
          aria-label="Next testimonial"
          className="flex h-12 w-12 items-center justify-center text-lg border-2 border-background-200 bg-background-50 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all duration-300 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>

      {/* Dots */}
      <div className="flex gap-2 mt-4">
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === activeIndex ? 'w-6 h-2 bg-primary-500' : 'w-2 h-2 bg-background-300 hover:bg-background-400'
            }`}
            aria-label={`Go to testimonial ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export { StaggerTestimonials };